<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="pageTitle"
      [canGoBack]="true"
      [noBow]="true"
      [showDropdownMenu]="canDelete"
      [dropdownMenu]="headerMenu" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<!-- The reservation a row TIDY wrote belongs to: one pick is the whole action, so it saves on the spot. -->
<mat-menu #reservationMenu="matMenu" class="txn-reservation-menu">
  @for (item of reservationItems; track item.value) {
    <button
      mat-menu-item
      type="button"
      [class.selected]="item.value === form.controls.guest_reservation_id.value"
      (click)="pickReservation(item.value)">{{ item.viewValue }}</button>
  }
</mat-menu>

<ng-template #content>
  <form [formGroup]="form">
    <!-- The day the money moved: what cash counts the row by. Declared inside the form so formControlName finds it. -->
    <ng-template #paidOnField>
      <tidy-row>
        <tidy-datepicker
          [label]="'Paid on'"
          formControlName="transacted_on"
          [form]="form.controls.transacted_on"
          [icon]="'assets/svg/cash-outline.svg'" />
      </tidy-row>
      <tidy-row class="txn-field-help">
        <tidy-text [helper]="paidOnHelp" />
        @if (paidOnResetLabel) {
          <span>&nbsp;</span>
          <tidy-text
            [actionBody]="paidOnResetLabel"
            (action)="resetPaidOn()"
            class="link" />
        }
      </tidy-row>
    </ng-template>

    <tidy-wrapper [class]="isRightSideContent ? 'finance-skeleton right-side-scroll-enabled' : 'finance-skeleton'">
      @if (!loaded) {
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <ion-skeleton-text [animated]="true" style="width: 55%; height: 22px; border-radius: 4px" />
          <ion-skeleton-text [animated]="true" style="width: 18%; height: 22px; border-radius: 4px" />
        </div>
        @for (i of [1,2,3]; track i) {
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
            <ion-skeleton-text [animated]="true" style="width: 18px; height: 18px; border-radius: 50%" />
            <ion-skeleton-text [animated]="true" style="width: 40%; height: 14px; border-radius: 4px" />
          </div>
        }
        @for (i of [1,2,3,4]; track i) {
          <ion-skeleton-text [animated]="true" style="width: 100%; height: 44px; border-radius: 8px; margin: 12px 0 0;" />
        }
        <ion-skeleton-text [animated]="true" style="width: 100%; height: 48px; border-radius: 24px; margin-top: 20px;" />
      }

      @if (loaded && errorMessage && !transaction && !bankTransaction) {
        <tidy-error-message [text]="errorMessage" />
      }

      @if (loaded && (transaction || bankTransaction)) {
        <tidy-alert class="black-background">
          <tidy-row [alignLastItemRight]="true">
            <tidy-text
              [header]="headline"
              [translate]="false" />
            <tidy-text
              [header]="formattedAmount"
              [translate]="false" />
          </tidy-row>
          <tidy-row class="vertical-align-center">
            <tidy-image
              [src]="'assets/svg/calendar-outline.svg'"
              class="body-size" />
            <tidy-text
              [body]="dateLabel"
              [translate]="false" />
            @if (isUpcoming) {
              <span class="txn-tag">Upcoming</span>
            }
          </tidy-row>
          @if (paidOnLine) {
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/cash-outline.svg'"
                class="body-size" />
              <tidy-text
                [body]="paidOnLine"
                [translate]="false" />
            </tidy-row>
          }
          @if (monthShareLine) {
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/pie-chart-outline.svg'"
                class="body-size" />
              <tidy-text
                [body]="monthShareLine"
                [translate]="false" />
            </tidy-row>
          }
          <tidy-row class="vertical-align-center">
            <tidy-image
              [src]="'assets/svg/business-outline.svg'"
              class="body-size" />
            <tidy-text
              [body]="sourceLabel"
              [translate]="false" />
          </tidy-row>
          @if (readOnly && jobId) {
            <tidy-row class="vertical-align-center txn-record-row">
              <tidy-image
                [src]="jobIconUrl"
                class="body-size" />
              @if (jobSummaryLabel) {
                <tidy-text
                  [body]="jobSummaryLabel"
                  [translate]="false" />
              }
              <tidy-text
                [actionBody]="'View job'"
                (action)="openJob()"
                class="white-link" />
            </tidy-row>
          }
          @if (readOnly && (reservationId || canLinkReservation)) {
            <tidy-row class="vertical-align-center txn-record-row">
              <tidy-image
                [src]="reservationIcon"
                class="body-size" />
              @if (reservationSummaryLabel) {
                <tidy-text
                  [body]="reservationSummaryLabel"
                  [translate]="false" />
              }
              @if (reservationId) {
                <tidy-text
                  [actionBody]="'View reservation'"
                  (action)="openReservation()"
                  class="white-link" />
              }
              @if (canLinkReservation) {
                <span class="txn-edit-record" [matMenuTriggerFor]="reservationMenu">
                  <tidy-text
                    [actionBody]="'Edit reservation'"
                    class="white-link" />
                  <span class="txn-edit-record__caret">▾</span>
                </span>
              }
            </tidy-row>
          }
          @if (!readOnly && (mode === 'add-to-pnl' || transaction?.source === 'bank')) {
            <tidy-row class="vertical-align-center create-rule-link">
              <tidy-image
                [src]="'assets/svg/return-down-forward-outline.svg'"
                class="body-size" />
              <tidy-text
                [actionBody]="'Create a rule for transactions like this'"
                (action)="createRule()"
                class="white-link" />
            </tidy-row>
          }
          @if (readOnly) {
            <tidy-divider class="txn-alert-divider" />
            <tidy-row>
              <tidy-text
                [body]="'TIDY added this from your ' + sourceRecordKind + '. Its category, property, date and description follow that record, so it cannot be edited or removed here.'"
                [translate]="false" />
            </tidy-row>
            <tidy-row class="extra-top-padding vertical-align-center">
              <tidy-image
                [src]="'assets/svg/list-outline.svg'"
                class="body-size" />
              <tidy-text
                [body]="'Category: ' + (categoryLabel || 'None')"
                [translate]="false" />
            </tidy-row>
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/home-outline.svg'"
                class="body-size" />
              <tidy-text
                [body]="'Property: ' + (propertyLabel || 'None')"
                [translate]="false" />
            </tidy-row>
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/create-outline.svg'"
                class="body-size" />
              <tidy-text
                [body]="'Description: ' + (transaction?.description || '—')"
                [translate]="false" />
            </tidy-row>
            @if (itemViews.length > 0) {
              <tidy-row class="extra-top-padding">
                <tidy-text [header]="'What this includes'" />
              </tidy-row>
              @for (item of itemViews; track item.label + item.amountLabel) {
                <tidy-row [alignLastItemRight]="true">
                  <tidy-text
                    [body]="item.label"
                    [translate]="false" />
                  <tidy-text
                    [body]="item.amountLabel"
                    [translate]="false" />
                </tidy-row>
              }
            }
          }
        </tidy-alert>

        @if (readOnly && canCorrectPaidOn) {
          <ng-container *ngTemplateOutlet="paidOnField" />
          <tidy-button
            [text]="'Save paid date'"
            [action]="boundSavePaidOn"
            [disabled]="saving"
            class="primary" />
        }

        @if (readOnly && errorMessage) {
          <tidy-error-message [text]="errorMessage" />
        }

        @if (!readOnly) {
          @if (mode === 'add-to-pnl') {
            <tidy-alert>
              <tidy-row>
                <tidy-text [body]="'This bank row was left out of your P&L. Pick a category and property to add it.'" />
              </tidy-row>
            </tidy-alert>
          }

          @if (suggestionNote) {
            <tidy-alert>
              <tidy-row>
                <tidy-text [body]="suggestionNote" [translate]="false" />
              </tidy-row>
            </tidy-alert>
          }

          <tidy-row class="extra-top-padding">
            <tidy-select
              [label]="'Category'"
              formControlName="monthly_finance_type_key"
              [form]="form.controls.monthly_finance_type_key"
              [items]="categoryItems"
              [icon]="'assets/svg/list-outline.svg'"
              [submitted]="submitted"
              [errorMessage]="'Please select a category'" />
          </tidy-row>
          <tidy-row>
            <tidy-select
              [label]="'Property'"
              formControlName="address_id"
              [form]="form.controls.address_id"
              [items]="propertyItems"
              [icon]="'assets/svg/home-outline.svg'"
              [submitted]="submitted"
              [errorMessage]="'Please select a property'" />
          </tidy-row>
          <tidy-row>
            <tidy-datepicker
              [label]="'Date'"
              formControlName="date"
              [form]="form.controls.date"
              [submitted]="submitted"
              [errorMessage]="'Please select a date'" />
          </tidy-row>
          @if (mode === 'edit' && canSpread) {
            <tidy-row>
              <tidy-select
                [label]="'Spread'"
                formControlName="spread"
                [form]="form.controls.spread"
                [items]="spreadItems"
                [hideSearch]="true"
                [icon]="'assets/svg/calendar-outline.svg'" />
            </tidy-row>
            @if (form.controls.spread.value === 'custom') {
              <tidy-row>
                <tidy-datepicker
                  [label]="'Last day it covers'"
                  formControlName="spread_end"
                  [form]="form.controls.spread_end"
                  [minDate]="form.controls.date.value"
                  [submitted]="submitted"
                  [errorMessage]="'Pick a last day on or after the date'" />
              </tidy-row>
            }
            @if (spreadSummary) {
              <tidy-row class="txn-field-help">
                <tidy-text
                  [helper]="spreadSummary"
                  [translate]="false" />
              </tidy-row>
            }
          }
          @if (mode === 'edit' && canCorrectPaidOn) {
            <ng-container *ngTemplateOutlet="paidOnField" />
          }
          @if (mode === 'edit') {
            <tidy-row>
              <tidy-textarea
                [label]="'Description'"
                formControlName="description"
                [form]="form.controls.description"
                [icon]="'assets/svg/create-outline.svg'"
                [submitted]="submitted" />
            </tidy-row>
            @if (showReservationSection) {
              <tidy-row>
                <tidy-select
                  [label]="'Reservation'"
                  formControlName="guest_reservation_id"
                  [form]="form.controls.guest_reservation_id"
                  [items]="reservationItems"
                  [icon]="reservationIcon" />
              </tidy-row>
              @if (form.controls.guest_reservation_id.value) {
                <tidy-row>
                  <tidy-text
                    [actionBody]="'View the reservation'"
                    (action)="openReservation()"
                    class="link" />
                </tidy-row>
              }
            }
          }

          @if (errorMessage) {
            <tidy-error-message [text]="errorMessage" />
          }

          <tidy-button
            [text]="mode === 'add-to-pnl' ? 'Add to P&L' : 'Save'"
            [action]="boundSave"
            [disabled]="saving"
            class="primary" />
        }
      }
    </tidy-wrapper>
  </form>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
